/* Generated from Java with JSweet 2.3.5 - http://www.jsweet.org */
import { IVisitor } from '../visitors/IVisitor';
import { IVisitable } from './IVisitable';
import { Document } from './Document';
import { ValidationProblem } from './ValidationProblem';
import { NodePath } from './NodePath';
import { ValidationProblemSeverity } from './ValidationProblemSeverity';

/**
 * Base class for all node types in all data models.
 * @author eric.wittmann@gmail.com
 * @class
 */
export abstract class Node implements IVisitable {
    static __modelIdCounter : number = 0;

    public _ownerDocument : Document;

    public _parent : Node;

    _modelId : number = Node.__modelIdCounter++;

    _attributes : any;

    /**
     * Properties that are present in the source document,
     * but are not defined in the specification for this node,
     * so they can't be read directly into the data model fields.
     */
    _extraProperties : any;

    _validationProblems : any = <any>({});

    /**
     * Gets the owner document.
     * @return {Document}
     */
    public ownerDocument() : Document {
        return this._ownerDocument;
    }

    /**
     * Returns true if this node is extensible.
     * @return {boolean}
     */
    public isExtensible() : boolean {
        return false;
    }

    /**
     * Gets the parent.
     * @return {Node}
     */
    public parent() : Node {
        return this._parent;
    }

    /**
     * Gets the model's unique ID.
     * @return {number}
     */
    public modelId() : number {
        return this._modelId;
    }

    /**
     * @see io.apicurio.datamodels.core.models.IVisitable#accept(io.apicurio.datamodels.core.visitors.IVisitor)
     * @param {*} visitor
     */
    public abstract accept(visitor : IVisitor);

    /**
     * Gets a single attribute by name.
     * @param {string} attributeName
     * @return {*}
     */
    public getAttribute(attributeName : string) : any {
        if(this._attributes != null) {
            return /* get */((m,k) => m[k]===undefined?null:m[k])(this._attributes, attributeName);
        } else {
            return null;
        }
    }

    /**
     * Sets a single named attribute value.
     * @param {string} attributeName
     * @param {*} attributeValue
     */
    public setAttribute(attributeName : string, attributeValue : any) {
        if(this._attributes == null) {
            this._attributes = <any>({});
        }
        /* put */(this._attributes[attributeName] = attributeValue);
    }

    /**
     * Gets a collection of all the attribute names.
     * @return {string[]}
     */
    public getAttributeNames() : Array<string> {
        if(this._attributes != null) {
            return /* keySet */Object.keys(this._attributes);
        } else {
            return /* emptyList */[];
        }
    }

    /**
     * Deletes all attributes in the node.
     */
    public clearAttributes() {
        if(this._attributes != null) {
            /* clear */(obj => { for (let member in obj) delete obj[member]; })(this._attributes);
        }
    }

    /**
     * Gets a list of all the validation problem codes for this node (and this node only).  Returns
     * an empty list if no problems are found for this node.
     * @return {string[]}
     */
    public getValidationProblemCodes() : Array<string> {
        return <any>(/* keySet */Object.keys(this._validationProblems).slice(0));
    }

    /**
     * Gets a list of all the validation problems detected for this node.
     * @return {ValidationProblem[]}
     */
    public getValidationProblems() : Array<ValidationProblem> {
        return <any>(/* values */(obj => Object.keys(obj).map(key => obj[key]))(this._validationProblems).slice(0));
    }

    /**
     * Gets a list of all validation problems detected for this node, filtered by only those
     * problems that apply to the given property name.
     * @param {string} propertyName
     * @return {ValidationProblem[]}
     */
    public getValidationProblemsFor(propertyName : string) : Array<ValidationProblem> {
        let rval : Array<ValidationProblem> = <any>([]);
        {
            let array201 = /* values */(obj => Object.keys(obj).map(key => obj[key]))(this._validationProblems);
            for(let index200=0; index200 < array201.length; index200++) {
                let problem = array201[index200];
                {
                    if(/* equals */(<any>((o1: any, o2: any) => { if(o1 && o1.equals) { return o1.equals(o2); } else { return o1 === o2; } })(problem.property,propertyName))) {
                        /* add */(rval.push(problem)>0);
                    }
                }
            }
        }
        return rval;
    }

    /**
     * Adds a validation problem to the data model.  Typically this is only used by the validation
     * layer when performing validation on the data model.
     * @param {string} errorCode
     * @param {NodePath} nodePath
     * @param {string} property
     * @param {string} message
     * @param {ValidationProblemSeverity} severity
     * @return {ValidationProblem}
     */
    public addValidationProblem(errorCode : string, nodePath : NodePath, property : string, message : string, severity : ValidationProblemSeverity) : ValidationProblem {
        let problem : ValidationProblem = new ValidationProblem(errorCode, nodePath, property, message, severity);
        /* put */(this._validationProblems[errorCode] = problem);
        return problem;
    }

    /**
     * Bulk add validation problems to the data model.
     * 
     * @param {ValidationProblem[]} problems
     */
    public addValidationProblems(problems : Array<ValidationProblem>) {
        for(let index202=0; index202 < problems.length; index202++) {
            let problem = problems[index202];
            {
                this.addValidationProblem(problem.errorCode, problem.nodePath, problem.property, problem.message, problem.severity);
            }
        }
    }

    /**
     * Deletes all validation problems previously discovered for this node.  Typically called by
     * the validation layer to reset the data model prior to performing validation.
     */
    public clearValidationProblems() {
        /* clear */(obj => { for (let member in obj) delete obj[member]; })(this._validationProblems);
    }

    /**
     * Adds an extra property to the data model.  This is called when the reader encounters a property
     * that is unexpected based on the expected schema.
     * @param {string} key
     * @param {*} value
     */
    public addExtraProperty(key : string, value : any) {
        if(this._extraProperties == null) {
            this._extraProperties = <any>({});
        }
        /* put */(this._extraProperties[key] = value);
    }

    public removeExtraProperty(name : string) : any {
        if(this._extraProperties != null && /* containsKey */this._extraProperties.hasOwnProperty(name)) {
            return /* remove */(map => { let deleted = this._extraProperties[name];delete this._extraProperties[name];return deleted;})(this._extraProperties);
        }
        return null;
    }

    public hasExtraProperties() : boolean {
        return this._extraProperties != null && /* size */Object.keys(this._extraProperties).length > 0;
    }

    public getExtraPropertyNames() : Array<string> {
        if(this.hasExtraProperties()) {
            return <any>(/* keySet */Object.keys(this._extraProperties).slice(0));
        }
        return /* emptyList */[];
    }

    public getExtraProperty(name : string) : any {
        if(this.hasExtraProperties()) {
            return /* get */((m,k) => m[k]===undefined?null:m[k])(this._extraProperties, name);
        }
        return null;
    }

    /**
     * Determine if this node has a parent and owner document defined.
     * 
     * @throws java.lang.IllegalStateException if the state is inconsistent, i.e. one is set but not the other
     * @return {boolean}
     */
    public isAttached() : boolean {
        if(this._parent == null || this._ownerDocument == null) {
            if(this._parent == null && this._ownerDocument == null) return false; else throw Object.defineProperty(new Error("Partially attached."), '__classes', { configurable: true, value: ['java.lang.Throwable','java.lang.IllegalStateException','java.lang.Object','java.lang.RuntimeException','java.lang.Exception'] });
        }
        return true;
    }

    /**
     * Set this {@link io.apicurio.datamodels.core.models.Node} to have the argument as its parent,
     * and the same {@link io.apicurio.datamodels.core.models.Document}.
     * 
     * Warning: The parent MUST attach this child node separately.
     * 
     * @throws java.lang.IllegalArgumentException if the parent is not attached itself
     * @throws java.lang.IllegalStateException if the parent's {@link Node#isAttached()} throws the exception
     * @param {Node} parent
     */
    public attachToParent(parent : Node) {
        if(!parent.isAttached()) throw Object.defineProperty(new Error("Target parent node (method argument) is not itself attached."), '__classes', { configurable: true, value: ['java.lang.Throwable','java.lang.Object','java.lang.RuntimeException','java.lang.IllegalArgumentException','java.lang.Exception'] });
        this._ownerDocument = parent.ownerDocument();
        this._parent = parent;
    }

    constructor() {
        if(this._ownerDocument===undefined) this._ownerDocument = null;
        if(this._parent===undefined) this._parent = null;
        if(this._attributes===undefined) this._attributes = null;
        if(this._extraProperties===undefined) this._extraProperties = null;
    }
}
Node["__class"] = "io.apicurio.datamodels.core.models.Node";
Node["__interfaces"] = ["io.apicurio.datamodels.core.models.IVisitable"];




